import { createComponentStore } from '../../utils/createComponentStore';
import { normalizeV2ToV1 } from '../../utils/enumNormalization';

const keyAuto = () => 'document' + (Math.floor(Math.random() * 90000000) + 10000).toString();

const initData = () => ({
  outputKey: keyAuto(),
  sourceType: 'EDITOR',
  exportFormat: 'DOCX',
  content: '',
});

function onLoadData(rawData) {
  const data = { ...rawData };
  if (data.key != null) { data.outputKey = data.key; delete data.key; }
  if (data.type != null) { data.sourceType = data.type; delete data.type; }
  if (data.export != null) { data.exportFormat = data.export; delete data.export; }
  return data;
}

const { getStore: getDocumentStore, defaultStore } = createComponentStore({
  type: 'document_editor',
  endpoint: 'document-editor',
  registryKey: 'document-editor',
  initData,

  onLoadData,

  extraState: () => ({
    files: [],
    fileUrl: [],
  }),

  extraActions: (set, get) => ({
    // Override setComponentData to extract files/fileUrl from data into top-level state
    setComponentData: (component) => {
      const cd = component.componentData;
      const updates = {
        componentId: component.componentId,
        type: component.type,
      };

      if (cd) {
        const isV2 = cd.schemaVersion >= 2;
        let rawData;
        let filesSource;

        if (isV2) {
          // v2: fields flat at root of componentData
          const { schemaVersion, name, lastModified, files: f, fileUrl: fu, ...rest } = cd;
          rawData = rest;
          filesSource = { f, fu, cdFiles: undefined, cdFileUrl: undefined };
          updates.name = name || '';
          updates.lastModified = lastModified || '';
        } else if (cd.data != null) {
          // v1: fields under componentData.data
          const { files: f, fileUrl: fu, ...coreData } = cd.data;
          rawData = coreData;
          filesSource = { f, fu, cdFiles: cd.files, cdFileUrl: cd.fileUrl };
          updates.name = cd.name || '';
          updates.lastModified = cd.lastModified || '';
        } else {
          // Legacy fallback
          const { name, lastModified, files: f, fileUrl: fu, ...rest } = cd;
          const { files: f2, fileUrl: fu2, ...coreRest } = rest;
          rawData = coreRest;
          filesSource = { f: f ?? f2, fu: fu ?? fu2 };
          updates.name = name || '';
          updates.lastModified = lastModified || '';
        }

        rawData = normalizeV2ToV1('document_editor', rawData);
        updates.data = onLoadData({ ...initData(), ...rawData });
        updates.files = filesSource.f ?? filesSource.cdFiles ?? [];
        updates.fileUrl = filesSource.fu ?? filesSource.cdFileUrl ?? [];
      } else {
        updates.data = initData();
        updates.files = [];
        updates.fileUrl = [];
        updates.name = '';
        updates.lastModified = '';
      }

      set(updates);
    },

    // Backward-compat alias used by DocumentTemplate REST fallback
    setData: (component) => get().setComponentData(component),

    setContent: (value, type) => {
      set((s) => {
        const data = { ...s.data };
        switch (type) {
          case 'key':
          case 'outputKey':
            data.outputKey = value;
            break;
          case 'type':
          case 'sourceType':
            data.sourceType = value;
            data.content = '';
            // Keep files and fileUrl intact so the user doesn't lose their
            // uploaded template or URL when switching modes. The backend only
            // executes the currently selected sourceType.
            // Update the prefill snapshot so storeWriteGuard Gate 4 doesn't
            // mistake this for a data wipe.
            {
              const newSnapshot = JSON.stringify(data);
              return { data, _prefillSnapshot: newSnapshot };
            }
          case 'content':
            data.content = value;
            break;
          case 'export':
          case 'exportFormat':
            data.exportFormat = value;
            break;
          default:
            break;
        }
        return { data };
      });
    },

    setFiles: (files) => set({ files }),

    setFileUrl: (fileUrl) => set({ fileUrl }),

    // Override getTemplate to merge files/fileUrl back into data for saving
    getTemplate: () => {
      const s = get();
      return {
        componentId: s.componentId,
        name: s.name,
        type: s.type,
        lastModified: s.lastModified,
        data: { ...s.data, files: s.files, fileUrl: s.fileUrl },
      };
    },
  }),
});

export { getDocumentStore };
export default defaultStore;
